import type {
  ActionFlowExecutionOptions,
  ActionFlowExecutionTrace,
  ActionFlowStepTrace,
} from '@cogo/runtime-v2'
import {
  ACTION_FLOW_TRACE_CONTRACT_VERSION,
  ActionFlowEngine,
  StateManager,
} from '@cogo/runtime-v2'
import type { Flow } from '@cogo/types-v2'
import type {
  ActionFlowActionCall,
  ActionFlowDataSource,
  ActionFlowRendererProjection,
  ActionFlowStateDomain,
  ActionFlowStateDiff,
  ActionFlowStepEvidence,
  ActionFlowTraceEntry,
} from '../types'

const WRAPPED = Symbol.for('cogo.studio.preview.actionflowTraceWrapped')
const RUNTIME_INSTANCE = Symbol.for('cogo.runtime.instanceId')

const SCENARIO_EXECUTION_CONTEXT_KEY = '__cogoScenarioExecutionId'

type TraceableEngine = ActionFlowEngine & {
  [WRAPPED]?: boolean
  [RUNTIME_INSTANCE]?: string
  executeFlow: (
    flow: Flow,
    context?: Record<string, unknown>,
    options?: ActionFlowExecutionOptions,
  ) => Promise<void>
  executeFlowWithTrace?: (
    flow: Flow,
    context?: Record<string, unknown>,
    options?: ActionFlowExecutionOptions,
  ) => Promise<ActionFlowExecutionTrace>
  findFlow: (name: string) => Flow | undefined
  getRegisteredFlowNames: () => string[]
}

export interface PreviewEventHint {
  elementId: string
  eventType: string
  eventValue?: unknown
}

interface InstallTraceOptions {
  stateManager: StateManager
  actionEngine: ActionFlowEngine
  getEventHint: () => PreviewEventHint | null
  getRendererProjection?: () => ActionFlowRendererProjection[]
  getProductionBinding?: () => {
    projectId: string
    projectRevision: number
  } | null
  onTrace: (trace: ActionFlowTraceEntry) => void
}

export function installActionFlowTraceCapture({
  stateManager,
  actionEngine,
  getEventHint,
  getRendererProjection,
  getProductionBinding,
  onTrace,
}: InstallTraceOptions): void {
  const engine = actionEngine as TraceableEngine
  if (engine[WRAPPED]) return
  const runtimeInstanceId = engine[RUNTIME_INSTANCE] ?? createRuntimeInstanceId()
  engine[RUNTIME_INSTANCE] = runtimeInstanceId

  const originalExecuteFlow = engine.executeFlow.bind(engine)
  const originalExecuteFlowWithTrace = engine.executeFlowWithTrace?.bind(engine)

  // executeFlowWithTrace enters the public executeFlow method once to run the
  // controlled execution. Suppress only that synchronous driver re-entry.
  // Unlike the former promise-depth flag, this does not hide a concurrent
  // top-level execution, so join/reject duplicate evidence reaches Studio.
  let traceDriverStarting = false
  const startRuntimeTrace = originalExecuteFlowWithTrace
    ? (
        flow: Flow,
        context?: Record<string, unknown>,
        options?: ActionFlowExecutionOptions,
      ) => {
        traceDriverStarting = true
        try {
          return originalExecuteFlowWithTrace(flow, context, options)
        } finally {
          traceDriverStarting = false
        }
      }
    : undefined

  engine.executeFlow = async (
    flow: Flow,
    context?: Record<string, unknown>,
    options?: ActionFlowExecutionOptions,
  ) => {
    if (traceDriverStarting) return originalExecuteFlow(flow, context, options)
    return await tracedExecuteFlow(flow, context, options)
  }

  const tracedExecuteFlow = async (
    flow: Flow,
    context?: Record<string, unknown>,
    options?: ActionFlowExecutionOptions,
  ) => {
    const productionBinding = readProductionTraceBinding({
      getProductionBinding,
      runtimeInstanceId,
      context,
    })
    const startedAt = performance.now()
    const before = snapshotState(stateManager)
    const rendererProjectionBefore = getRendererProjection?.() ?? []
    const flowName = resolveFlowName(engine, flow)
    const hint = getEventHint()
    const eventLabel = hint ? `${hint.elementId}:${hint.eventType}` : 'runtime'
    const eventValue = hint?.eventValue

    try {
      const runtimeTrace = await executeWithOptionalRuntimeTrace({
        flow,
        context,
        options,
        originalExecuteFlow,
        startRuntimeTrace,
      })
      await waitForRendererFrame()
      onTrace(buildTraceEntry({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        eventLabel,
        flow,
        flowName,
        eventValue,
        source: 'preview',
        status: 'pass',
        durationMs: Math.round(performance.now() - startedAt),
        before,
        after: snapshotState(stateManager),
        runtimeTrace,
        results: pickNamespace(stateManager, '_results'),
        computed: pickNamespace(stateManager, 'computed'),
        rendererProjectionBefore,
        rendererProjection: getRendererProjection?.() ?? [],
        productionBinding,
      }))
    } catch (err) {
      const runtimeTrace = readRuntimeTraceFromError(err)
      await waitForRendererFrame()
      onTrace(buildTraceEntry({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        eventLabel,
        flow,
        flowName,
        eventValue,
        source: 'preview',
        status: 'error',
        durationMs: Math.round(performance.now() - startedAt),
        before,
        after: snapshotState(stateManager),
        runtimeTrace,
        results: pickNamespace(stateManager, '_results'),
        computed: pickNamespace(stateManager, 'computed'),
        rendererProjectionBefore,
        rendererProjection: getRendererProjection?.() ?? [],
        productionBinding,
        error: err instanceof Error ? err.message : String(err),
      }))
      throw err
    }
  }

  engine[WRAPPED] = true
}

interface BuildTraceEntryOptions {
  id: string
  eventLabel: string
  flow: Flow
  flowName: string
  eventValue?: unknown
  source?: 'preview' | 'dry-run'
  status: 'pass' | 'error'
  durationMs: number
  before: Record<string, unknown>
  after: Record<string, unknown>
  runtimeTrace?: ActionFlowExecutionTrace | null
  results: Record<string, unknown>
  computed: Record<string, unknown>
  rendererProjectionBefore?: ActionFlowRendererProjection[]
  rendererProjection: ActionFlowRendererProjection[]
  productionBinding?: ProductionActionFlowTraceBinding
  error?: string
}

interface ProductionActionFlowTraceBinding {
  projectId: string
  projectRevision: number
  runtimeInstanceId: string
  scenarioExecutionId?: string
}

function buildTraceEntry({
  id,
  eventLabel,
  flow,
  flowName,
  eventValue,
  source,
  status,
  durationMs,
  before,
  after,
  runtimeTrace,
  results,
  computed,
  rendererProjectionBefore,
  rendererProjection,
  productionBinding,
  error,
}: BuildTraceEntryOptions): ActionFlowTraceEntry {
  const stateDiffs = runtimeTrace
    ? diffSnapshots(runtimeTrace.startState, runtimeTrace.finalState)
    : diffSnapshots(before, after)
  const rawStepTrace = runtimeTrace
    ? mapRuntimeStepTrace(runtimeTrace.steps)
    : collectStaticActionFlowStepEvidence(flow)
  const rawActionCalls = runtimeTrace
    ? collectRuntimeActionFlowActionCalls(runtimeTrace, flow)
    : collectActionFlowActionCalls(flow)
  const { stepTrace, actionCalls } = normalizeDryRunDomainOperationEvidence({
    flow,
    source,
    stepTrace: rawStepTrace,
    actionCalls: rawActionCalls,
  })
  const unsupportedAction = stepTrace.find((step) => step.action && step.status === 'skipped')
  const caughtError = stepTrace.find((step) => step.caughtError)?.caughtError
  const runtimeStatus = mapRuntimeExecutionStatus(runtimeTrace?.status)
  const candidateStatus = runtimeStatus ?? status
  const effectiveStatus = candidateStatus === 'pass' && unsupportedAction ? 'error' : candidateStatus
  const effectiveError = error ?? runtimeTrace?.error ?? (unsupportedAction
    ? `Unsupported ActionFlow action '${unsupportedAction.action}' was skipped by the runtime`
    : undefined)
  return {
    id,
    contractVersion: runtimeTrace?.contractVersion ?? ACTION_FLOW_TRACE_CONTRACT_VERSION,
    traceId: runtimeTrace?.traceId ?? id,
    eventLabel,
    ...(eventValue !== undefined ? { eventValue } : {}),
    flowName,
    ...(productionBinding ?? {}),
    ...(source ? { source } : {}),
    status: effectiveStatus,
    ...(runtimeTrace?.executionId ? { executionId: runtimeTrace.executionId } : {}),
    ...(runtimeTrace?.startedAt ? { startedAt: runtimeTrace.startedAt } : {}),
    ...(runtimeTrace?.finishedAt ? { finishedAt: runtimeTrace.finishedAt } : {}),
    ...(runtimeTrace?.idempotencyKey ? { idempotencyKey: runtimeTrace.idempotencyKey } : {}),
    ...(runtimeTrace?.duplicateOfExecutionId
      ? { duplicateOfExecutionId: runtimeTrace.duplicateOfExecutionId }
      : {}),
    durationMs: runtimeTrace?.durationMs ?? durationMs,
    stateDiffs,
    stateDomains: Array.from(new Set(stateDiffs.map((diff) => diff.domain))).sort(),
    actionCalls,
    stepTrace,
    ...(caughtError ? { caughtError } : {}),
    results,
    computed,
    ...(rendererProjectionBefore ? { rendererProjectionBefore } : {}),
    rendererProjection,
    ...(effectiveStatus === 'error' || effectiveStatus === 'timed-out'
      ? { blockedScope: inferBlockedScope(stateDiffs, actionCalls) }
      : {}),
    ...(effectiveError ? { error: effectiveError } : {}),
  }
}

function createRuntimeInstanceId(): string {
  const randomId = typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `runtime:${randomId}`
}

function readProductionTraceBinding({
  getProductionBinding,
  runtimeInstanceId,
  context,
}: {
  getProductionBinding?: InstallTraceOptions['getProductionBinding']
  runtimeInstanceId: string
  context?: Record<string, unknown>
}): ProductionActionFlowTraceBinding | undefined {
  const binding = getProductionBinding?.()
  if (
    !binding
    || !binding.projectId
    || !Number.isInteger(binding.projectRevision)
    || binding.projectRevision < 0
  ) {
    return undefined
  }
  const eventContext = context?.event
  const nestedScenarioExecutionId = eventContext
    && typeof eventContext === 'object'
    && !Array.isArray(eventContext)
    ? (eventContext as Record<string, unknown>)[SCENARIO_EXECUTION_CONTEXT_KEY]
    : undefined
  const scenarioExecutionId = context?.[SCENARIO_EXECUTION_CONTEXT_KEY]
    ?? nestedScenarioExecutionId
  return {
    projectId: binding.projectId,
    projectRevision: binding.projectRevision,
    runtimeInstanceId,
    ...(typeof scenarioExecutionId === 'string' && scenarioExecutionId
      ? { scenarioExecutionId }
      : {}),
  }
}

export function readInstalledTraceRuntimeInstanceId(appId?: string): string | null {
  if (typeof window === 'undefined') return null
  if (appId) {
    const scopedBridgeId = (window as unknown as {
      __cogo_app_tools__?: Record<string, { runtimeInstanceId?: unknown } | undefined>
    }).__cogo_app_tools__?.[appId]?.runtimeInstanceId
    return typeof scopedBridgeId === 'string' && scopedBridgeId ? scopedBridgeId : null
  }
  const bridgeId = (window as unknown as {
    __cogo_tools__?: { runtimeInstanceId?: unknown }
  }).__cogo_tools__?.runtimeInstanceId
  if (typeof bridgeId === 'string' && bridgeId) return bridgeId

  const engine = (window as unknown as {
    __cogoRuntime?: { actionEngine?: TraceableEngine }
  }).__cogoRuntime?.actionEngine
  return engine?.[RUNTIME_INSTANCE] ?? null
}

export function readStudioTraceProjectBinding(state: {
  currentProjectId: string | null
  projectMeta: { id: string; version: number } | null
}): { projectId: string; projectRevision: number } | null {
  return state.currentProjectId
    && state.projectMeta?.id === state.currentProjectId
    && Number.isInteger(state.projectMeta.version)
    && state.projectMeta.version >= 0
    ? {
        projectId: state.currentProjectId,
        projectRevision: state.projectMeta.version,
      }
    : null
}

function mapRuntimeExecutionStatus(
  status: ActionFlowExecutionTrace['status'] | undefined,
): ActionFlowTraceEntry['status'] | undefined {
  if (!status) return undefined
  return status === 'ok' ? 'pass' : status
}

/**
 * A deterministic empty Response the dry-run fetch stub returns in place of a
 * real network round-trip. `restApi`/`graphql` read `resp.json()` (→ null) and
 * `resp.headers.get('content-range')` (→ null), so the step traces as a benign
 * empty result instead of firing a real request. Built per call so the body is
 * never "already consumed" across steps.
 */
function makeDryRunStubResponse(): Response {
  if (typeof Response === 'function') {
    return new Response('{}', {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    })
  }
  // Minimal shape for environments without a global Response (defensive).
  return {
    ok: true,
    status: 200,
    headers: { get: () => null } as unknown as Headers,
    json: async () => ({}),
    text: async () => '{}',
  } as unknown as Response
}

/**
 * Run a single flow OFF-CANVAS through a fresh, isolated, SIDE-EFFECT-FREE
 * headless runtime and map the result into the same ActionFlowTraceEntry the
 * live preview path produces (via the shared buildTraceEntry — no parallel
 * mapper to drift).
 *
 * The "Run (dry)" editor affordance (F5): there was previously no way to
 * execute a flow inside the editor — authors had to switch to Preview and
 * hand-fire UI events. This builds a brand-new StateManager + ActionFlowEngine
 * exactly per the proven F2 recipe (legacy-action-aliases.test.ts
 * probeStepStatus), so the run CANNOT touch any live preview state/store: a
 * fresh, empty, permissive StateManager is the entire in-memory blast radius.
 *
 * SIDE-EFFECT SUPPRESSION (honest "live state untouched"):
 *   - Platform-delegated actions (storage, payment, crypto, mediaCapture,
 *     geolocation, websocket, share, …) are no-ops: we pass NO PlatformAdapter,
 *     and every such executor early-returns on `!this.platform`. So none of them
 *     perform real I/O.
 *   - Network actions (restApi/restful_data_action, graphqlAction)
 *     do NOT go through the platform adapter — they call the GLOBAL `fetch`
 *     directly inside @cogo/runtime-v2 (action-flow-engine.ts),
 *     with no injection point we may use (runtime-v2 src must not be modified).
 *     We therefore scope-override `globalThis.fetch` for the duration of the run
 *     with a stub returning a deterministic empty 200 response, and restore the
 *     original in `finally`. A creator dry-running a flow with a restApi step
 *     fires NO real HTTP request (GET/POST/PUT/DELETE alike) — the network is
 *     stubbed, not merely state-isolated.
 *
 * Mock context is intentionally MINIMAL — empty state. Steps reading
 * #{event.*}/#{props.*}/#{route.*} see undefined and trace as benign
 * skipped/error, which is the honest dry-run result. No plausible inputs are
 * fabricated.
 *
 * The returned entry carries source: 'dry-run' so it never masquerades as a
 * real preview run nor flips the "verified in preview" badge
 * (deriveFlowVerification ignores dry-run traces), and the caller writes it to
 * the store's dedicated `dryRunTrace` slot (NOT addActionFlowTrace) so it never
 * enters the shared preview buffer.
 *
 * No @cogo/runtime-v2 src is modified — only its public exports
 * (StateManager, ActionFlowEngine, registerFlows, executeFlowWithTrace).
 */
export async function runFlowDryRun(flow: Flow): Promise<ActionFlowTraceEntry> {
  const startedAt = performance.now()
  const flowName = String((flow as { name?: unknown }).name ?? 'unknown flow')
  const id = `dry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

  // Fresh, isolated runtime — the F2 parity-gate recipe verbatim. NO platform
  // adapter: all platform-delegated side-effecting executors no-op (!this.platform).
  const stateManager = new StateManager(
    { state: {} },
    { localState: {}, inputSymbols: [], outputSymbols: [] },
    { permissive: true },
  )
  const engine = new ActionFlowEngine(stateManager)
  engine.registerAction('domain-operation', async (params) => ({
    mocked: true,
    mockKind: 'webmcp-domain-operation',
    sideEffects: 'suppressed',
    params,
  }))
  const runtimeFlow = prepareDryRunRuntimeFlow(flow)
  engine.registerFlows({ [flowName]: runtimeFlow } as never)

  const before = snapshotState(stateManager)

  // Scope-override the global fetch so the platform-independent network actions
  // (restApi/graphql/gemini) cannot reach the wire. Restored in finally — a
  // concurrent real preview run after the dry-run uses the genuine fetch.
  const realFetch = typeof globalThis.fetch === 'function' ? globalThis.fetch : undefined
  ;(globalThis as { fetch?: typeof fetch }).fetch = (async () =>
    makeDryRunStubResponse()) as typeof fetch

  try {
    const runtimeTrace = await engine.executeFlowWithTrace(runtimeFlow, {})
    return buildTraceEntry({
      id,
      eventLabel: 'dry-run',
      flow,
      flowName,
      source: 'dry-run',
      status: 'pass',
      durationMs: Math.round(performance.now() - startedAt),
      before,
      after: snapshotState(stateManager),
      runtimeTrace,
      results: pickNamespace(stateManager, '_results'),
      computed: pickNamespace(stateManager, 'computed'),
      rendererProjection: [],
    })
  } catch (err) {
    // The engine attaches the partial trace to the thrown error
    // (action-flow-engine.ts Object.assign(error, { actionFlowTrace })).
    const runtimeTrace = readRuntimeTraceFromError(err)
    return buildTraceEntry({
      id,
      eventLabel: 'dry-run',
      flow,
      flowName,
      source: 'dry-run',
      status: 'error',
      durationMs: Math.round(performance.now() - startedAt),
      before,
      after: snapshotState(stateManager),
      runtimeTrace,
      results: pickNamespace(stateManager, '_results'),
      computed: pickNamespace(stateManager, 'computed'),
      rendererProjection: [],
      error: err instanceof Error ? err.message : String(err),
    })
  } finally {
    if (realFetch) (globalThis as { fetch?: typeof fetch }).fetch = realFetch
    else delete (globalThis as { fetch?: typeof fetch }).fetch
  }
}

async function executeWithOptionalRuntimeTrace({
  flow,
  context,
  options,
  originalExecuteFlow,
  startRuntimeTrace,
}: {
  flow: Flow
  context?: Record<string, unknown>
  options?: ActionFlowExecutionOptions
  originalExecuteFlow: TraceableEngine['executeFlow']
  startRuntimeTrace: TraceableEngine['executeFlowWithTrace'] | undefined
}): Promise<ActionFlowExecutionTrace | null> {
  if (!startRuntimeTrace) {
    await originalExecuteFlow(flow, context, options)
    return null
  }

  return await startRuntimeTrace(flow, context, options)
}

function readRuntimeTraceFromError(error: unknown): ActionFlowExecutionTrace | null {
  if (!error || typeof error !== 'object') return null
  const trace = (error as { actionFlowTrace?: unknown }).actionFlowTrace
  if (!trace || typeof trace !== 'object') return null
  const candidate = trace as Partial<ActionFlowExecutionTrace>
  if (!Array.isArray(candidate.steps)) return null
  if (!candidate.startState || !candidate.finalState) return null
  return candidate as ActionFlowExecutionTrace
}

export function getPreviewEventHint(event: Event): PreviewEventHint | null {
  const target = event.target instanceof Element ? event.target : null
  if (!target) return null
  const element = target.closest('[data-cogo-element-id], [id], [data-testid]')
  const rawId =
    element?.getAttribute('data-cogo-element-id') ??
    element?.getAttribute('id') ??
    element?.getAttribute('data-testid') ??
    ''
  if (!rawId) return null

  const elementId = rawId.startsWith('input-') ? rawId.slice('input-'.length) : rawId
  const eventType = event.type === 'click' ? 'tap' : event.type
  const eventValue = readEventValue(target)
  return {
    elementId,
    eventType,
    ...(eventValue !== undefined ? { eventValue } : {}),
  }
}

export function collectRendererProjection(
  container: HTMLElement | null,
): ActionFlowRendererProjection[] {
  if (!container) return []
  const nodes = Array.from(container.querySelectorAll<HTMLElement>('[data-testid^="text-"]'))

  const seen = new Set<string>()
  const projection: ActionFlowRendererProjection[] = []

  for (const node of nodes) {
    const elementId =
      node.getAttribute('data-cogo-element-id') ??
      node.getAttribute('data-testid') ??
      ''
    const text = (node.textContent ?? '').replace(/\s+/g, ' ').trim()
    if (!elementId || !text || seen.has(`${elementId}:${text}`)) continue
    seen.add(`${elementId}:${text}`)
    projection.push({ elementId, text })
    if (projection.length >= 64) break
  }

  return projection
}

function waitForRendererFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      return
    }
    setTimeout(resolve, 0)
  })
}

function resolveFlowName(engine: TraceableEngine, flow: Flow): string {
  for (const name of engine.getRegisteredFlowNames()) {
    if (engine.findFlow(name) === flow) return name
  }
  return String((flow as unknown as Record<string, unknown>).name ?? 'unknown flow')
}

function snapshotState(stateManager: StateManager): Record<string, unknown> {
  const snapshot = stateManager.getSymbolStore().getSnapshot()
  return JSON.parse(JSON.stringify(snapshot)) as Record<string, unknown>
}

function pickNamespace(stateManager: StateManager, namespace: string): Record<string, unknown> {
  const value = stateManager.get(namespace)
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return JSON.parse(JSON.stringify(value)) as Record<string, unknown>
}

function diffSnapshots(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): ActionFlowStateDiff[] {
  const paths = new Set([...flattenPaths(before), ...flattenPaths(after)])
  const diffs: ActionFlowStateDiff[] = []

  for (const path of Array.from(paths).sort()) {
    const beforeValue = getPath(before, path)
    const afterValue = getPath(after, path)
    if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
      diffs.push({
        path,
        domain: classifyActionFlowStatePath(path),
        before: beforeValue,
        after: afterValue,
      })
    }
  }

  return diffs
}

export function classifyActionFlowStatePath(path: string): ActionFlowStateDomain {
  if (!path) return 'unknown'
  if (path === 'appData' || path.startsWith('appData.')) return 'application_global'
  if (path === '_results' || path.startsWith('_results.')) return 'runtime'
  if (path === 'computed' || path.startsWith('computed.')) return 'runtime'
  if (path === 'ui' || path.startsWith('ui.')) return 'ui'
  if (path === 'route' || path.startsWith('route.')) return 'ui'
  if (path === 'env' || path.startsWith('env.')) return 'external'
  if (path === 'props' || path.startsWith('props.')) return 'external'
  if (path === 'event' || path.startsWith('event.')) return 'external'
  return 'block_logic'
}

export function collectActionFlowActionCalls(flow: Flow): ActionFlowActionCall[] {
  return collectStaticActionFlowStepEvidence(flow)
    .filter((step) => step.action)
    .map((step) => {
      const params = getStepParamsFromEvidence(step)
      const action = step.action ?? step.type
      const dataSource = classifyActionDataSource(action, params)
      const nestedFlow = readNestedFlowName(params)
      return {
        order: step.index,
        ...(step.parentIndex !== undefined ? { parentIndex: step.parentIndex } : {}),
        path: step.path,
        stepType: step.type,
        action,
        dataSource,
        inputSources: inferInputSources({ params, detail: step.detail }),
        status: step.status,
        ...(step.detail ? { detail: step.detail } : {}),
        ...(nestedFlow ? { nestedFlow } : {}),
        ...(step.error ? { error: step.error } : {}),
        ...(step.caughtError ? { caughtError: step.caughtError } : {}),
        ...(dataSource === 'local_db' || dataSource === 'external' || dataSource === 'local_runtime'
          ? extractActionCallDetails(params)
          : {}),
      }
    })
}

export function collectStaticActionFlowStepEvidence(flow: Flow): ActionFlowStepEvidence[] {
  const collector: StaticStepCollector = { index: 0, steps: [] }
  flattenStaticSteps(flow.steps ?? [], 'steps', collector)
  return collector.steps
}

interface StaticStepCollector {
  index: number
  steps: ActionFlowStepEvidence[]
}

function flattenStaticSteps(
  steps: Flow['steps'],
  basePath: string,
  collector: StaticStepCollector,
  parentIndex?: number,
): void {
  steps.forEach((step, index) => {
    const path = `${basePath}.${index}`
    const stepType = String(step.type ?? 'basic')
    const action = getStepAction(step)
    const detail = extractStaticStepDetail(step)
    const stepIndex = collector.index++
    collector.steps.push({
      index: stepIndex,
      ...(parentIndex !== undefined ? { parentIndex } : {}),
      path,
      type: stepType,
      ...(action ? { action } : {}),
      ...('name' in step && typeof step.name === 'string' ? { name: step.name } : {}),
      status: 'planned',
      ...(detail ? { detail } : {}),
      stateChanges: [],
    })

    if (step.type === 'conditional') {
      flattenStaticSteps(step.then ?? [], `${path}.then`, collector, stepIndex)
      flattenStaticSteps(step.else ?? [], `${path}.else`, collector, stepIndex)
    } else if (step.type === 'loop' || step.type === 'parallel') {
      flattenStaticSteps(step.steps ?? [], `${path}.steps`, collector, stepIndex)
    } else if (step.type === 'tryCatch') {
      flattenStaticSteps(step.try ?? [], `${path}.try`, collector, stepIndex)
      flattenStaticSteps(step.catch ?? [], `${path}.catch`, collector, stepIndex)
      flattenStaticSteps(step.finally ?? [], `${path}.finally`, collector, stepIndex)
    } else if (step.type === 'switch') {
      for (const [caseName, caseSteps] of Object.entries(step.cases ?? {})) {
        flattenStaticSteps(caseSteps, `${path}.cases.${caseName}`, collector, stepIndex)
      }
      flattenStaticSteps(step.default ?? [], `${path}.default`, collector, stepIndex)
    } else if (step.type === 'stage') {
      flattenStaticSteps(step.onEnter ?? [], `${path}.onEnter`, collector, stepIndex)
      flattenStaticSteps(step.steps ?? [], `${path}.steps`, collector, stepIndex)
      flattenStaticSteps(step.onSuccess ?? [], `${path}.onSuccess`, collector, stepIndex)
      flattenStaticSteps(step.onError ?? [], `${path}.onError`, collector, stepIndex)
      flattenStaticSteps(step.onFinally ?? [], `${path}.onFinally`, collector, stepIndex)
      flattenStaticSteps(step.compensate ?? [], `${path}.compensate`, collector, stepIndex)
    } else if (step.type === 'saga') {
      flattenStaticSteps(step.stages ?? [], `${path}.stages`, collector, stepIndex)
    }
  })
}

function extractStaticStepDetail(step: Flow['steps'][number]): Record<string, unknown> | undefined {
  if (step.type === 'conditional') {
    return {
      condition: step.condition,
      thenCount: step.then.length,
      elseCount: step.else?.length ?? 0,
    }
  }
  if (step.type === 'loop') {
    return {
      collection: step.collection,
      itemVar: step.itemVar,
      stepCount: step.steps.length,
    }
  }
  if (step.type === 'parallel') return { stepCount: step.steps.length }
  if (step.type === 'tryCatch') {
    return { tryCount: step.try.length, catchCount: step.catch?.length ?? 0, finallyCount: step.finally?.length ?? 0 }
  }
  if (step.type === 'switch') {
    return {
      value: step.value,
      cases: Object.keys(step.cases ?? {}),
      defaultCount: step.default?.length ?? 0,
    }
  }
  if (step.type === 'stage') {
    return {
      name: step.name,
      failurePolicy: step.failurePolicy ?? 'stop',
      bodyCount: step.steps.length,
      onEnterCount: step.onEnter?.length ?? 0,
      onSuccessCount: step.onSuccess?.length ?? 0,
      onErrorCount: step.onError?.length ?? 0,
      onFinallyCount: step.onFinally?.length ?? 0,
      compensationCount: step.compensate?.length ?? 0,
    }
  }
  if (step.type === 'saga') {
    return {
      stageCount: step.stages.length,
      compensationFailurePolicy: step.compensationFailurePolicy ?? 'continue',
    }
  }
  const params = getStepParams(step)
  const nestedFlow = readNestedFlowName(params)
  if (params && nestedFlow) return { nestedCallFlow: nestedFlow, params }
  return params ? { params } : undefined
}

function mapRuntimeStepTrace(steps: ActionFlowStepTrace[]): ActionFlowStepEvidence[] {
  return steps.map((step) => {
    const caughtError = step.type === 'tryCatch' && typeof step.detail?.error === 'string'
      ? step.detail.error
      : undefined
    const detail = buildRuntimeStepDetail(step)
    return {
      index: step.index,
      ...(step.parentIndex !== undefined ? { parentIndex: step.parentIndex } : {}),
      // Engine-recorded syntactic path (steps.N…); '#index' only for traces
      // from engines that predate path recording.
      path: step.path ?? `#${step.index}`,
      type: step.type,
      ...(step.action ? { action: step.action } : {}),
      ...(step.name ? { name: step.name } : {}),
      status: step.status,
      ...(detail ? { detail } : {}),
      stateChanges: step.stateChanges.map((change) => ({
        path: change.path,
        domain: classifyActionFlowStatePath(change.path),
        before: change.before,
        after: change.after,
      })),
      ...(step.error ? { error: step.error } : {}),
      ...(caughtError ? { caughtError } : {}),
      ...(step.result !== undefined ? { result: step.result } : {}),
    }
  })
}

function collectRuntimeActionFlowActionCalls(trace: ActionFlowExecutionTrace, flow: Flow): ActionFlowActionCall[] {
  const staticActionSteps = collectStaticActionFlowStepEvidence(flow).filter((step) => step.action)
  let actionOrdinal = 0
  return trace.steps
    .filter((step) => step.action)
    .map((step) => {
      // Prefer the engine-recorded syntactic path. The flat executed-order
      // ordinal drifts under loops (bodies repeat per iteration) and
      // conditionals (untaken branches never execute), misattributing the
      // wrong definition path to a call. The ordinal remains only as a
      // fallback for traces from engines that predate `step.path`.
      const ordinalStep = staticActionSteps[actionOrdinal++]
      const params = step.params ?? null
      const action = step.action ?? step.type
      const dataSource = classifyActionDataSource(action, params)
      const detail = buildRuntimeStepDetail(step)
      const nestedFlow = readNestedFlowName(params)
      const caughtError = step.type === 'tryCatch' && typeof step.detail?.error === 'string'
        ? step.detail.error
        : undefined
      return {
        order: step.index,
        ...(step.parentIndex !== undefined ? { parentIndex: step.parentIndex } : {}),
        path: step.path ?? ordinalStep?.path ?? `#${step.index}`,
        stepType: step.type,
        action,
        dataSource,
        inputSources: inferInputSources({ params: step.params, detail: step.detail }),
        status: step.status,
        ...(detail ? { detail } : {}),
        ...(nestedFlow ? { nestedFlow } : {}),
        ...(step.error ? { error: step.error } : {}),
        ...(caughtError ? { caughtError } : {}),
        ...(dataSource === 'local_db' || dataSource === 'external' || dataSource === 'local_runtime'
          ? extractActionCallDetails(params)
          : {}),
      }
    })
}

function normalizeDryRunDomainOperationEvidence({
  flow,
  source,
  stepTrace,
  actionCalls,
}: {
  flow: Flow
  source?: 'preview' | 'dry-run'
  stepTrace: ActionFlowStepEvidence[]
  actionCalls: ActionFlowActionCall[]
}): {
  stepTrace: ActionFlowStepEvidence[]
  actionCalls: ActionFlowActionCall[]
} {
  if (source !== 'dry-run') return { stepTrace, actionCalls }

  const staticDomainSteps = collectStaticActionFlowStepEvidence(flow).filter(isDomainOperationStepEvidence)
  if (staticDomainSteps.length === 0) return { stepTrace, actionCalls }
  const staticDomainStepByPath = new Map(staticDomainSteps.map((step) => [step.path, step]))

  const normalizedSteps = stepTrace.map((step) =>
    isDomainOperationStepEvidence(step)
      ? markDomainOperationStepMocked(mergeStaticDomainOperationStep(step, staticDomainStepByPath.get(step.path)))
      : step,
  )
  const normalizedCalls = actionCalls.map((call) =>
    isDomainOperationActionCall(call)
      ? markDomainOperationActionCallMocked(mergeStaticDomainOperationCall(call, staticDomainStepByPath.get(call.path)))
      : call,
  )

  const existingStepPaths = new Set(normalizedSteps.map((step) => step.path))
  const existingCallPaths = new Set(normalizedCalls.map((call) => call.path))

  for (const staticStep of staticDomainSteps) {
    if (!existingStepPaths.has(staticStep.path)) {
      normalizedSteps.push(markDomainOperationStepMocked(staticStep))
    }
    if (!existingCallPaths.has(staticStep.path)) {
      normalizedCalls.push(buildDomainOperationMockActionCall(staticStep))
    }
  }

  return {
    stepTrace: normalizedSteps.sort((a, b) => a.index - b.index),
    actionCalls: normalizedCalls.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
  }
}

function prepareDryRunRuntimeFlow(flow: Flow): Flow {
  return {
    ...flow,
    steps: mapDryRunRuntimeSteps(flow.steps ?? []),
  } as Flow
}

function mapDryRunRuntimeSteps(steps: Flow['steps']): Flow['steps'] {
  return steps.map((step) => {
    if (isDomainOperationLike(step)) {
      return {
        type: 'basic',
        action: 'domain-operation',
        params: getStepParams(step) ?? {},
      } as Flow['steps'][number]
    }
    if (step.type === 'conditional') {
      return {
        ...step,
        then: mapDryRunRuntimeSteps(step.then ?? []),
        else: mapDryRunRuntimeSteps(step.else ?? []),
      }
    }
    if (step.type === 'loop' || step.type === 'parallel') {
      return {
        ...step,
        steps: mapDryRunRuntimeSteps(step.steps ?? []),
      }
    }
    if (step.type === 'tryCatch') {
      const catchSteps = step.catch
      const finallySteps = step.finally
      return {
        ...step,
        try: mapDryRunRuntimeSteps(step.try ?? []),
        ...(catchSteps !== undefined
          ? { catch: mapDryRunRuntimeSteps(catchSteps) }
          : {}),
        ...(finallySteps !== undefined
          ? { finally: mapDryRunRuntimeSteps(finallySteps) }
          : {}),
      }
    }
    if (step.type === 'switch') {
      return {
        ...step,
        cases: Object.fromEntries(
          Object.entries(step.cases ?? {}).map(([caseName, caseSteps]) => [
            caseName,
            mapDryRunRuntimeSteps(caseSteps),
          ]),
        ),
        default: mapDryRunRuntimeSteps(step.default ?? []),
      }
    }
    if (step.type === 'stage') {
      return {
        ...step,
        steps: mapDryRunRuntimeSteps(step.steps),
        ...(step.onEnter !== undefined ? { onEnter: mapDryRunRuntimeSteps(step.onEnter) } : {}),
        ...(step.onSuccess !== undefined ? { onSuccess: mapDryRunRuntimeSteps(step.onSuccess) } : {}),
        ...(step.onError !== undefined ? { onError: mapDryRunRuntimeSteps(step.onError) } : {}),
        ...(step.onFinally !== undefined ? { onFinally: mapDryRunRuntimeSteps(step.onFinally) } : {}),
        ...(step.compensate !== undefined ? { compensate: mapDryRunRuntimeSteps(step.compensate) } : {}),
      }
    }
    if (step.type === 'saga') {
      return {
        ...step,
        stages: mapDryRunRuntimeSteps(step.stages) as typeof step.stages,
      }
    }
    return step
  }) as Flow['steps']
}

function mergeStaticDomainOperationStep(
  step: ActionFlowStepEvidence,
  staticStep?: ActionFlowStepEvidence,
): ActionFlowStepEvidence {
  if (!staticStep) return step
  return {
    ...step,
    type: staticStep.type,
    action: staticStep.action ?? step.action,
    detail: {
      ...(staticStep.detail ?? {}),
      ...(step.detail ?? {}),
    },
  }
}

function mergeStaticDomainOperationCall(
  call: ActionFlowActionCall,
  staticStep?: ActionFlowStepEvidence,
): ActionFlowActionCall {
  if (!staticStep) return call
  return {
    ...call,
    stepType: staticStep.type,
    action: staticStep.action ?? call.action,
    detail: {
      ...(staticStep.detail ?? {}),
      ...(call.detail ?? {}),
    },
  }
}

function markDomainOperationStepMocked(step: ActionFlowStepEvidence): ActionFlowStepEvidence {
  const { error: _error, ...rest } = step
  return {
    ...rest,
    action: step.action ?? 'domain-operation',
    status: 'ok',
    detail: buildDomainOperationMockDetail(step.detail),
    stateChanges: step.stateChanges ?? [],
  }
}

function markDomainOperationActionCallMocked(call: ActionFlowActionCall): ActionFlowActionCall {
  const { error: _error, ...rest } = call
  return {
    ...rest,
    action: call.action || 'domain-operation',
    dataSource: 'external',
    status: 'ok',
    detail: buildDomainOperationMockDetail(call.detail),
  }
}

function buildDomainOperationMockActionCall(step: ActionFlowStepEvidence): ActionFlowActionCall {
  const params = getStepParamsFromEvidence(step)
  return {
    order: step.index,
    ...(step.parentIndex !== undefined ? { parentIndex: step.parentIndex } : {}),
    path: step.path,
    stepType: step.type,
    action: step.action ?? 'domain-operation',
    dataSource: 'external',
    inputSources: inferInputSources({ params, detail: step.detail }),
    status: 'ok',
    detail: buildDomainOperationMockDetail(step.detail),
    ...extractActionCallDetails(params),
  }
}

function buildDomainOperationMockDetail(detail?: Record<string, unknown>): Record<string, unknown> {
  return {
    ...(detail ?? {}),
    mocked: true,
    mockKind: 'webmcp-domain-operation',
    mockReason: 'Dry-run records the source-derived WebMCP/domain operation without calling live tools.',
    sideEffects: 'suppressed',
  }
}

function getStepParamsFromEvidence(step: ActionFlowStepEvidence): Record<string, unknown> | null {
  const params = step.detail?.params
  if (!params || typeof params !== 'object' || Array.isArray(params)) return null
  return params as Record<string, unknown>
}

function getStepAction(step: Flow['steps'][number]): string | undefined {
  if ('action' in step && typeof step.action === 'string') return step.action
  if (isDomainOperationLike(step)) return 'domain-operation'
  return undefined
}

function getStepParams(step: Flow['steps'][number]): Record<string, unknown> | null {
  if ('params' in step && step.params && typeof step.params === 'object' && !Array.isArray(step.params)) {
    return step.params as Record<string, unknown>
  }
  const record = step as unknown as Record<string, unknown>
  const directParams: Record<string, unknown> = {}
  for (const key of ['target', 'sourceExpression', 'tool', 'operation', 'intent', 'query']) {
    if (record[key] !== undefined) directParams[key] = record[key]
  }
  return Object.keys(directParams).length > 0 ? directParams : null
}

function buildRuntimeStepDetail(step: ActionFlowStepTrace): Record<string, unknown> | undefined {
  const detail: Record<string, unknown> = {}
  if (step.detail && typeof step.detail === 'object') Object.assign(detail, step.detail)
  if (step.params && Object.keys(step.params).length > 0) {
    const nestedFlow = readNestedFlowName(step.params)
    if (nestedFlow) detail.nestedCallFlow = nestedFlow
    detail.params = step.params
  }
  return Object.keys(detail).length > 0 ? detail : undefined
}

function readNestedFlowName(params?: Record<string, unknown> | null): string | undefined {
  const flow = params?.flow
  if (typeof flow === 'string' && flow.trim()) return flow
  const flowName = params?.flowName
  if (typeof flowName === 'string' && flowName.trim()) return flowName
  return undefined
}

function extractActionCallDetails(params?: Record<string, unknown> | null): Pick<ActionFlowActionCall, 'target' | 'operation'> {
  if (!params) return {}
  const target =
    typeof params.target === 'string'
      ? params.target
      : typeof params.resultTarget === 'string'
        ? params.resultTarget
        : undefined
  const operation =
    typeof params.operation === 'string'
      ? params.operation
      : typeof params.method === 'string'
        ? params.method.toUpperCase()
        : undefined
  return {
    ...(target ? { target } : {}),
    ...(operation ? { operation } : {}),
  }
}

function classifyActionDataSource(
  action: string,
  params?: Record<string, unknown> | null,
): ActionFlowDataSource {
  const normalized = action.toLowerCase()
  if (normalized === 'domain-operation' || normalized.includes('webmcp')) return 'external'
  if (
    normalized === 'setstate' &&
    typeof params?.operation === 'string' &&
    ['append', 'upsert', 'updateWhere', 'removeWhere'].includes(params.operation)
  ) {
    return 'local_db'
  }
  if (normalized.includes('storage') || normalized.includes('localdb')) return 'local_db'
  if (
    normalized.includes('rest') ||
    normalized.includes('graphql') ||
    normalized.includes('supabase') ||
    normalized.includes('websocket') ||
    normalized.includes('payment') ||
    normalized.includes('gemini')
  ) {
    return 'external'
  }
  if (
    normalized.includes('setstate') ||
    normalized.includes('setvariable') ||
    normalized.includes('compute') ||
    normalized.includes('callflow') ||
    normalized.includes('validate')
  ) {
    return 'local_runtime'
  }
  if (
    normalized.includes('navigate') ||
    normalized.includes('toast') ||
    normalized.includes('popup') ||
    normalized.includes('loading') ||
    normalized.includes('scroll') ||
    normalized.includes('golink')
  ) {
    return 'none'
  }
  return 'unknown'
}

function isDomainOperationStepEvidence(step: ActionFlowStepEvidence): boolean {
  return isDomainOperationToken(step.action) || isDomainOperationToken(step.type)
}

function isDomainOperationActionCall(call: ActionFlowActionCall): boolean {
  return isDomainOperationToken(call.action) || isDomainOperationToken(call.stepType)
}

function isDomainOperationLike(step: Flow['steps'][number]): boolean {
  const record = step as unknown as Record<string, unknown>
  return isDomainOperationToken(record.action) || isDomainOperationToken(record.type)
}

function isDomainOperationToken(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase()
  return normalized === 'domain-operation' || normalized === 'webmcp'
}

function readEventValue(target: Element): unknown {
  if (target instanceof HTMLInputElement) {
    if (target.type === 'checkbox' || target.type === 'radio') return target.checked
    return target.value
  }
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
    return target.value
  }
  return undefined
}

function inferBlockedScope(
  stateDiffs: ActionFlowStateDiff[],
  actionCalls: ActionFlowActionCall[],
): ActionFlowStateDomain {
  const diffDomain = stateDiffs.find((diff) => diff.domain !== 'unknown')?.domain
  if (diffDomain) return diffDomain

  for (const call of actionCalls) {
    if (!call.target) continue
    const domain = classifyActionFlowStatePath(call.target)
    if (domain !== 'unknown') return domain
  }

  const inputSource = actionCalls.flatMap((call) => call.inputSources).find((source) => source !== 'unknown')
  if (inputSource === 'user_input' || inputSource === 'external') return 'external'
  if (inputSource === 'local_runtime') return 'block_logic'
  if (inputSource === 'local_db') return 'application_global'

  return 'unknown'
}

function inferInputSources(value: unknown): ActionFlowDataSource[] {
  const serialized = JSON.stringify(value) ?? ''
  const sources = new Set<ActionFlowDataSource>()
  if (serialized.includes('#{event.') || serialized.includes('#eventData')) sources.add('user_input')
  if (serialized.includes('#{local.')) sources.add('local_runtime')
  if (serialized.includes('#{appData.')) sources.add('local_runtime')
  if (serialized.includes('#{_results.') || serialized.includes('#_results')) sources.add('local_runtime')
  if (serialized.includes('#{props.') || serialized.includes('#{route.') || serialized.includes('#{env.')) {
    sources.add('external')
  }
  return Array.from(sources).sort()
}

function flattenPaths(value: unknown, prefix = ''): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return prefix ? [prefix] : []
  }
  const entries = Object.entries(value as Record<string, unknown>)
  if (entries.length === 0) return prefix ? [prefix] : []
  return entries.flatMap(([key, child]) => flattenPaths(child, prefix ? `${prefix}.${key}` : key))
}

function getPath(value: Record<string, unknown>, path: string): unknown {
  let current: unknown = value
  for (const part of path.split('.')) {
    if (!current || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return current
}
